import React from "react";
import {
  ArrowLeft,
  Clock,
  Tag,
  Calendar,
  User,
  MessageSquare,
  History,
  Link as LinkIcon,
  UserPlus,
  AlertCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type {
  TicketType,
  TicketStatus,
  TicketPriority,
} from "@/services/ticket.service";

import { formatDate } from "@/utils/formatDate.util";

interface TicketDetailViewProps {
  ticket: TicketType;
}

const STATUS_CONFIG: Record<
  TicketStatus,
  { label: string; className: string; icon: any }
> = {
  open: {
    label: "Open",
    className: "bg-blue-50 text-blue-600 border-blue-100",
    icon: Clock,
  },
  in_progress: {
    label: "In Progress",
    className: "bg-amber-50 text-amber-600 border-amber-100",
    icon: Clock,
  },
  resolved: {
    label: "Resolved",
    className: "bg-green-50 text-green-600 border-green-100",
    icon: Clock,
  },
  closed: {
    label: "Closed",
    className: "bg-gray-50 text-gray-600 border-gray-100",
    icon: Clock,
  },
};

const PRIORITY_CONFIG: Record<
  TicketPriority,
  { label: string; className: string }
> = {
  critical: {
    label: "Critical",
    className: "bg-red-50 text-red-600 border-red-100",
  },
  high: {
    label: "High",
    className: "bg-orange-50 text-orange-600 border-orange-100",
  },
  medium: {
    label: "Medium",
    className: "bg-blue-50 text-blue-600 border-blue-100",
  },
  low: { label: "Low", className: "bg-gray-50 text-gray-600 border-gray-100" },
};

const TicketDetailView: React.FC<TicketDetailViewProps> = ({ ticket }) => {
  const navigate = useNavigate();

  return (
    <div className="mx-auto min-h-screen max-w-[1600px] space-y-6 bg-gray-50/30 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/tickets")}
            className="h-10 w-10 rounded-full hover:bg-white hover:shadow-sm"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </Button>
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                {ticket.ticketNumber}
              </h1>
              <Badge
                variant="outline"
                className={cn(
                  "rounded-md px-2 py-0.5 font-medium",
                  STATUS_CONFIG[ticket.status].className
                )}
              >
                {STATUS_CONFIG[ticket.status].label}
              </Badge>
              <Badge
                variant="outline"
                className={cn(
                  "rounded-md px-2 py-0.5 font-medium",
                  PRIORITY_CONFIG[ticket.priority].className
                )}
              >
                {PRIORITY_CONFIG[ticket.priority].label}
              </Badge>
            </div>
            <p className="text-lg font-medium text-gray-600">{ticket.title}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="gap-2">
            Edit Ticket
          </Button>
          <Button variant="outline" className="gap-2">
            Send Message
          </Button>
          <Button variant="outline" className="gap-2">
            Edit Internship Vendor
          </Button>
          <Button variant="destructive" className="bg-red-600 hover:bg-red-700">
            Deactivate
          </Button>
        </div>
      </div>

      {/* Metric Cards Row */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[
          {
            label: "Status",
            value: STATUS_CONFIG[ticket.status].label,
            icon: Clock,
            color: "text-blue-500",
            bgColor: "bg-blue-50",
          },
          {
            label: "Category",
            value: ticket.category,
            icon: Tag,
            color: "text-purple-500",
            bgColor: "bg-purple-50",
          },
          {
            label: "Created",
            value: formatDate(ticket.createdAt, { dateStyle: "short" }),
            icon: Calendar,
            color: "text-green-500",
            bgColor: "bg-green-50",
          },
          {
            label: "Last Updated",
            value: formatDate(ticket.updatedAt, { dateStyle: "short" }),
            icon: Clock,
            color: "text-orange-500",
            bgColor: "bg-orange-50",
          },
        ].map((stat, idx) => (
          <Card
            key={idx}
            className="border-none shadow-sm transition-shadow hover:shadow-md"
          >
            <CardContent className="flex items-center gap-4 p-5">
              <div className={cn("shrink-0 rounded-xl p-2.5", stat.bgColor)}>
                <stat.icon className={cn("h-5 w-5", stat.color)} />
              </div>
              <div className="space-y-0.5">
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500">
                  {stat.label}
                </p>
                <p className="text-base font-bold text-gray-900">
                  {stat.value}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left Column: Description, Comments, Timeline */}
        <div className="space-y-6 lg:col-span-2">
          {/* Description Section */}
          <Card className="border-none shadow-sm">
            <CardHeader className="border-b border-gray-50 pb-3">
              <CardTitle className="flex items-center gap-2 text-lg font-bold">
                Description
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="whitespace-pre-wrap leading-relaxed text-gray-600">
                {ticket.description}
              </p>
            </CardContent>
          </Card>

          {/* Comments Section */}
          <Card className="overflow-hidden border-none shadow-sm">
            <CardHeader className="border-b border-gray-50 pb-3">
              <CardTitle className="flex items-center gap-2 text-lg font-bold">
                <MessageSquare className="h-5 w-5 text-gray-400" />
                Comments ({ticket.comments.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-gray-50">
                {ticket.comments.map((comment) => (
                  <div
                    key={comment.id}
                    className="space-y-3 p-6 transition-colors hover:bg-gray-50/50"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="space-y-0.5">
                          <p className="text-sm font-bold text-gray-900">
                            {comment.author}
                          </p>
                          <p className="text-xs font-medium text-gray-400">
                            {comment.role}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs font-medium text-gray-400">
                        {formatDate(comment.timestamp, {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                      </p>
                    </div>
                    <p className="pl-1 text-sm leading-relaxed text-gray-600">
                      {comment.content}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
            <CardFooter className="flex items-end gap-3 border-t border-gray-100 bg-gray-50/50 p-6">
              <div className="flex-1 space-y-1">
                <Input
                  placeholder="Add a comment..."
                  className="focus-visible:ring-primary flex h-24 items-start border-gray-200 bg-white pt-3"
                />
              </div>
              <Button className="h-10 px-6 font-medium">Add Comment</Button>
            </CardFooter>
          </Card>

          {/* Activity Timeline Section */}
          <Card className="border-none shadow-sm">
            <CardHeader className="border-b border-gray-50 pb-3">
              <CardTitle className="flex items-center gap-2 text-lg font-bold">
                <History className="h-5 w-5 text-gray-400" />
                Activity Timeline
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="relative space-y-8 pl-8 before:absolute before:bottom-2 before:left-[11px] before:top-2 before:w-0.5 before:bg-gray-100">
                {ticket.timeline.map((event) => (
                  <div key={event.id} className="group relative">
                    <div className="absolute -left-[30px] top-0.5">
                      <div className="flex h-[22px] w-[22px] items-center justify-center rounded-full border-[3px] border-white bg-blue-500 shadow-sm">
                        <div className="h-1.5 w-1.5 rounded-full bg-white" />
                      </div>
                    </div>
                    <div className="flex flex-col justify-between gap-1 md:flex-row md:items-center">
                      <div className="space-y-0.5">
                        <p className="text-sm font-bold text-gray-900">
                          {event.title}
                        </p>
                        <p className="text-xs font-medium text-gray-500">
                          by{" "}
                          <span className="text-gray-900">{event.author}</span>
                        </p>
                      </div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                        {formatDate(event.timestamp, {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                      </p>
                    </div>
                    {event.description && (
                      <p className="mt-2 text-xs leading-loose text-gray-500">
                        {event.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Raised By, Assigned To, Related Module, Actions */}
        <div className="space-y-6">
          {/* Raised By Section */}
          <Card className="group overflow-hidden border-none shadow-sm">
            <CardHeader className="flex flex-row items-center gap-2 border-b border-gray-50/50 pb-3">
              <User className="h-4 w-4 text-gray-400" />
              <CardTitle className="text-base font-bold">Raised By</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 pt-6">
              <div className="space-y-4">
                {[
                  { label: "Name", value: ticket.raisedBy.name },
                  {
                    label: "Type",
                    value: (
                      <Badge
                        variant="secondary"
                        className="rounded-md px-2 py-0 text-[10px] font-bold uppercase"
                      >
                        {ticket.raisedBy.role}
                      </Badge>
                    ),
                  },
                  { label: "Roll Number", value: ticket.raisedBy.rollNo },
                  {
                    label: "Email",
                    value: (
                      <span className="cursor-pointer text-blue-600 hover:underline">
                        {ticket.raisedBy.email}
                      </span>
                    ),
                  },
                  { label: "Phone", value: ticket.raisedBy.phone },
                ].map((item, idx) => (
                  <div key={idx} className="space-y-1">
                    <p className="text-[10px] font-bold uppercase leading-none tracking-widest text-gray-400">
                      {item.label}
                    </p>
                    <p className="truncate text-sm font-bold text-gray-900">
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Assigned To Section */}
          <Card className="overflow-hidden border-none shadow-sm">
            <CardHeader className="flex flex-row items-center gap-2 border-b border-gray-50/50 pb-3">
              <UserPlus className="h-4 w-4 text-gray-400" />
              <CardTitle className="text-base font-bold">Assigned To</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 pt-6">
              <div className="space-y-4">
                {[
                  { label: "Name", value: ticket.assignedTo.name },
                  { label: "Role", value: ticket.assignedTo.role },
                  {
                    label: "Email",
                    value: (
                      <span className="cursor-pointer text-blue-600 hover:underline">
                        {ticket.assignedTo.email}
                      </span>
                    ),
                  },
                  { label: "Phone", value: ticket.assignedTo.phone },
                ].map((item, idx) => (
                  <div key={idx} className="space-y-1">
                    <p className="text-[10px] font-bold uppercase leading-none tracking-widest text-gray-400">
                      {item.label}
                    </p>
                    <p className="truncate text-sm font-bold text-gray-900">
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Related Module Section */}
          {ticket.relatedModule && (
            <Card className="overflow-hidden border-none shadow-sm">
              <CardHeader className="flex flex-row items-center gap-2 border-b border-gray-50/50 pb-3">
                <LinkIcon className="h-4 w-4 text-gray-400" />
                <CardTitle className="text-base font-bold">
                  Related Module
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5 pt-6">
                <div className="space-y-4">
                  <div className="space-y-1">
                    <p className="text-[10px] font-bold uppercase leading-none tracking-widest text-gray-400">
                      Module
                    </p>
                    <p className="text-sm font-bold text-gray-900">
                      {ticket.relatedModule.type}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-bold uppercase leading-none tracking-widest text-gray-400">
                      Details
                    </p>
                    <p className="text-sm font-bold italic text-gray-500 text-gray-900">
                      {ticket.relatedModule.details}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    className="h-9 w-full border-gray-200 bg-white text-xs font-bold"
                  >
                    View Related {ticket.relatedModule.type}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions Section */}
          <Card className="overflow-hidden border-none shadow-sm">
            <CardHeader className="flex flex-row items-center gap-2 border-b border-gray-50/50 pb-3">
              <AlertCircle className="h-4 w-4 text-gray-400" />
              <CardTitle className="text-base font-bold">Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-6">
              <Button
                variant="outline"
                className="h-10 w-full border-gray-200 bg-white text-sm font-bold"
              >
                Update Status
              </Button>
              <Button
                variant="outline"
                className="h-10 w-full border-gray-200 bg-white text-sm font-bold"
              >
                Reassign Ticket
              </Button>
              <Button
                variant="outline"
                className="h-10 w-full border-gray-200 bg-white text-sm font-bold"
              >
                Change Priority
              </Button>
              <Button className="h-10 w-full bg-red-600 text-sm font-bold hover:bg-red-700">
                Close Ticket
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TicketDetailView;
